import { useAppConstantsContext } from '@ltvco/refresh-lib/ctx';
import {
  formattedFamilySearchData,
  getFamilySearch,
} from '@ltvco/refresh-lib/fmt';
import { useSession } from '@ltvco/refresh-lib/v1';
import {
  getAncestorSearchParams,
  type AdvancedSearchURLParams,
} from '@ltvco/refresh-lib/v2';
import { useCallback, useEffect, useState } from 'react';

import { NULL_SEARCH_PARAMS } from 'components/ChatBV/constants';

import type { AncestorSearchParams } from 'components/ChatBV/utils/conversationUtils';

function buildAdvancedSearchURLParamsFromAncestor(
  params: AncestorSearchParams
): AdvancedSearchURLParams {
  return {
    ...NULL_SEARCH_PARAMS,
    fName: params.first_name ?? '',
    lName: params.last_name ?? '',
    mName: params.middle_name ?? '',
    fullName: [params.first_name, params.middle_name, params.last_name]
      .filter(Boolean)
      .join(' ')
      .trim(),
    birthMonth: params.birth_month ?? null,
    birthYear: params.birth_year ?? null,
    deathMonth: params.death_month ?? null,
    deathYear: params.death_year ?? null,
    city: params.city ?? '',
    state: params.state ?? '',
    age: params.age ?? '',
  };
}

/**
 * Returns searchAncestor for handleFunctionCalls (ancestor/genealogy teaser).
 * Uses getFamilySearch from lib with ip, brandName and userCode from context.
 */
export function useAncestorSearchOption(): (
  params: AncestorSearchParams
) => Promise<{ results: unknown[]; count: number } | undefined> {
  const [ip, setIp] = useState('');
  const { data: appConstantsData } = useAppConstantsContext();
  const { useAccount } = useSession();
  const { data: accountData } = useAccount(true);

  const brandName = (appConstantsData?.brandName ?? '').toLowerCase();
  const userCode = accountData?.account?.user_info?.user_code ?? '';

  useEffect(() => {
    fetch('https://api.ipify.org?format=json')
      .then((r) => r.json())
      .then((d) => setIp(d.ip ?? ''))
      .catch(() => {});
  }, []);

  return useCallback(
    async (params: AncestorSearchParams) => {
      if (!ip || !userCode || !brandName) {
        return undefined;
      }
      try {
        const searchData = buildAdvancedSearchURLParamsFromAncestor(params);
        const postData = getAncestorSearchParams(searchData);
        const raw = await getFamilySearch(ip, {
          ...postData,
          page: 1,
          per_page: 100,
          brand_name: brandName,
          user_code: userCode,
        });
        if (!raw?.entities?.options?.people?.length) {
          return undefined;
        }
        const formatted = formattedFamilySearchData(
          raw as Parameters<typeof formattedFamilySearchData>[0],
          params.first_name ?? '',
          params.last_name ?? ''
        );
        return {
          results: formatted as unknown[],
          count: Array.isArray(formatted) ? formatted.length : 0,
        };
      } catch {
        return undefined;
      }
    },
    [ip, userCode, brandName]
  );
}
